import { redirect, type AnyRoute } from '@tanstack/react-router';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '@/auth/msal';
import { authModule } from '@/auth/auth-module';
import { isInteractionRequired, markInteractionRequired } from '@/auth/interaction-required';
import { getLoginHint, getAuth0LoginHint } from '@/auth/login-hint';
import {
  isAuth0Authenticated,
  isAuth0Configured,
  ensureGlobalAuth0Session,
  loginToAuth0,
} from '@/auth/auth0';
import {
  isAuth0App,
  resolveClientIdStrict,
  getShellAuthProvider,
  AUTH_PROVIDER_KEY,
} from '@/auth/auth-provider';
import { scopesForUrl } from '@/http/scope-map';
import { Endpoints } from '@/http/endpoints';
import { getTracker } from '@/telemetry/tracker';
import {
  ensureRoleScreens,
  isScreenAllowed,
  isEssentialOrAllowlistedScreen,
  hasRoleScreens,
  isRoleMappingBootstrapped,
} from '@/auth/role-screen-access';
import { isTileProvisioned } from '@/apps/use-app-tiles';
import { pushToast } from '@/ui/toast-store';
import { markLoginTime } from '@/auth/session-duration';
import { useClientStore, CLIENT_STORAGE_KEYS } from '@/client/client-store';

/** Toast shown when a user opens an app they are not entitled to in this client. */
const APP_ACCESS_DENIED_TOAST =
  "You don't have access to the requested URL. Redirecting to an available client.";

/**
 * Layered route-guard implementations migrated from
 * `raptor-shell-ui/src/app/core/guards/`:
 *
 *   1. {@link requireAuth}    — MasterRouteGuard ∘ BaseRouteGuard ∘ MsalGuard.
 *      Blocks navigation when no MSAL active account exists; kicks off MSAL
 *      `loginRedirect` so the user lands back on the requested URL.
 *   2. {@link resolveTenant}  — Replaces the implicit clientId-in-URL parsing
 *      in BaseRouteGuard. Stores the requested URL in sessionStorage so we
 *      can resume after login (matches legacy `SESSION_STORAGE.REQUEST_URL`).
 *   3. {@link validateRoute}  — Port of ValidateRouteGuard. For now a
 *      structural check (appId is a non-empty GUID-shaped string); full
 *      role-mapping lookup is M3.
 *
 * Each guard emits a `route_guard_blocked` AppInsights event on rejection
 * (per .docs/telemetry-baseline.md §5).
 */

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Allow lowercase-slug appIds in addition to legacy GUIDs so new MFEs
// (e.g. `myteams`, `orchestrator`) can route without being assigned a GUID.
// Constraints: starts with a letter, 2–63 chars total, [a-z0-9-_].
const APP_ID_SLUG_RE = /^[a-z][a-z0-9_-]{1,62}$/;
// Legacy `SESSION_STORAGE.REQUEST_URL` — shared key so the deep link resumes
// after auth (and stays cross-shell compatible during canary).
const REQUEST_URL_KEY = 'request.url';

interface GuardContext {
  location: { href: string; pathname: string };
}

/**
 * Throws a TanStack `redirect` to the supplied path; use within `beforeLoad`
 * to stop a navigation that fails authorisation. `to` must be a route id
 * registered on the router.
 */
function block(guard: string, reason: string, to: string): never {
  getTracker().event('route_guard_blocked', { guard, reason, redirect_target: to });
  throw redirect({ to });
}

/**
 * Stash the requested deep link (legacy `SESSION_STORAGE.REQUEST_URL`) so it can
 * be replayed by `FrameRedirectPage` after the AAD round-trip. Only resumable
 * in-app paths are stored (single leading slash; never an auth/error route).
 */
function stashRequestedUrl(href: string): void {
  if (!href || !href.startsWith('/') || href.startsWith('//')) return;
  const path = href.split('?')[0];
  if (path === '/login' || path === '/error' || path.startsWith('/frameredirect')) return;
  try {
    sessionStorage.setItem(REQUEST_URL_KEY, href);
  } catch {
    /* sessionStorage may be unavailable in some test environments */
  }
}

/**
 * Authentication guard. Mirrors legacy MsalGuard semantics:
 *  - if MSAL has no active account → kick off `loginRedirect` and stash the
 *    requested URL so the user resumes there after the AAD round-trip;
 *  - throws a TanStack redirect so router doesn't render the failed route.
 */
export async function requireAuth(ctx: GuardContext): Promise<void> {
  // Preserve the requested deep link in EVERY path that can lead to the login
  // page — interaction-required (below), silent-token failure, or no active
  // account — not just the no-account branch. Doing it here (before any early
  // exit) fixes deep links being dropped when a re-login is routed through the
  // /login page (legacy stashes REQUEST_URL in the guard; FrameRedirectPage
  // replays it).
  stashRequestedUrl(ctx.location.href);

  if (isInteractionRequired()) {
    block('requireAuth', 'interaction_required', '/login');
  }

  // Independent Auth0 shell session (VITE_AUTH_PROVIDER=auth0): authenticate
  // entirely through Auth0 with NO MSAL involvement — this is the Auth0 peer of
  // the MSAL branch below (session check → auto-redirect to the IdP on a miss).
  // The full-page redirect to Auth0 starts inside `loginToAuth0`; the trailing
  // `redirect('/login')` just halts this navigation cleanly (the document is
  // already leaving), mirroring the MSAL no-account branch.
  if (getShellAuthProvider() === 'auth0') {
    if (await isAuth0Authenticated()) {
      try {
        localStorage.setItem(AUTH_PROVIDER_KEY, 'auth0');
      } catch {
        /* storage unavailable */
      }
      return;
    }
    markLoginTime();
    await loginToAuth0({
      paramclientid: resolveClientIdStrict() ?? undefined,
      returnTo: ctx.location.pathname + window.location.search,
      loginHint: getAuth0LoginHint(),
    });
    throw redirect({ to: '/login', replace: true });
  }

  const account = msalInstance.getActiveAccount();
  if (account) {
    // Prime the GLOBAL Auth0 SSO session immediately after MSAL login (before
    // warming the MSAL token) so an Auth0-configured tenant establishes the
    // Auth0 session up-front — mirroring the deployed flow — instead of paying
    // an extra redirect only when the first Auth0 MFE is opened. When it starts
    // a redirect we halt this navigation with a /login redirect (the document is
    // already navigating to Auth0, same pattern as the no-account branch below).
    // No-op when Auth0 is not configured, so MSAL-only environments are unchanged.
    if (isAuth0Configured()) {
      const result = await ensureGlobalAuth0Session(ctx.location.pathname + window.location.search);
      if (result === 'redirecting') {
        throw redirect({ to: '/login', replace: true });
      }
    }

    // Legacy MsalGuard parity: don't just confirm an account object exists —
    // confirm a usable access token can be acquired SILENTLY before letting the
    // authenticated shell (and its client-list / roles / avatar fetches) mount.
    //
    // Checking only `getActiveAccount()` lets the shell mount against a stale
    // cached account, so the very first data request (the client list) is what
    // triggers `acquireTokenSilent` — i.e. the token/login round-trip races the
    // client request instead of preceding it. Awaiting the token here serialises
    // "login first, then everything else" and warms the token cache (keyed by
    // account+scopes) so the client request reuses it instead of issuing its own
    // parallel token call. We warm the RUM/token-service scope because the
    // client list, roles, avatar and user-details all use it.
    try {
      await authModule.getTokenAsync(scopesForUrl(Endpoints.rumUserClients()));
      return;
    } catch (err) {
      // `getTokenAsync` already routes an interaction-required failure to the
      // interactive login redirect; halt this navigation so nothing renders
      // against a half-authenticated session.
      if (err instanceof InteractionRequiredAuthError) {
        // `getTokenAsync` already kicked off the interactive login redirect.
        // Throw a TanStack `redirect` (NOT a plain Error) so the router treats
        // this as a navigation to /login instead of a render error — a plain
        // Error had no route errorComponent and bubbled to the shell_content
        // ErrorBoundary, flashing "Something went wrong" for the ~1-2s until the
        // auth round-trip re-navigated (seen on a direct-URL/new-tab app open).
        throw redirect({ to: '/login', replace: true });
      }
      // Transient (e.g. network) failure — let the route proceed so the shell
      // isn't hard-blocked on a blip; the individual request surfaces its own
      // error/retry.
      return;
    }
  }

  // Deep link already stashed at the top of the guard (stashRequestedUrl).

  getTracker().event('route_guard_blocked', {
    guard: 'requireAuth',
    reason: 'no_active_account',
    redirect_target: '/login',
  });

  // Anchor the absolute session-duration cap to this genuine login (legacy
  // `login.component` writes `login.time` when initiating login).
  markLoginTime();

  // Redirect to `/login` and let `LoginPage` own the AAD `loginRedirect`.
  // Legacy parity: `BaseRouteGuard` navigates to LOGIN_URL and the login
  // component initiates the redirect (showing a spinner, never a button).
  // Centralising initiation in ONE place removes the login-button flash that
  // showed while this guard's own `loginRedirect` was still building the AAD
  // URL, and prevents a double `loginRedirect` -> `interaction_in_progress`
  // race with `LoginPage`. Throw a TanStack `redirect` (NOT a plain Error) so
  // the router lands on `/login` rather than bubbling to the ErrorBoundary.
  throw redirect({ to: '/login', replace: true });
}

/**
 * Tenant resolution guard. Used by all routes nested under the authenticated
 * layer. Currently a no-op placeholder: the actual client switch lives in
 * M2.2 and writes to the same storage keys as legacy. Once that lands we
 * will redirect to a tenant-selection page when no `currentlySelectedClientId`
 * is present in storage.
 */
export function resolveTenant(_ctx: GuardContext): void {
  /* M2.2 will populate this; intentionally permissive for now. */
}

/**
 * Per-MFE route SHAPE gate. Rejects anything that isn't a legacy GUID or
 * slug appId (→ home). Matches case-insensitively (URL segment casing does
 * not affect validity), mirroring Angular's `validateRoute` which lowercases
 * the screen path before checking. This is the cheap structural check used by
 * the `/app/...` compat routes that only build a redirect target; the real
 * authorisation check is {@link validateScreenAccess} on the mount routes.
 */
export function validateRoute(appId: string): void {
  const normalized = appId?.toLowerCase() ?? '';
  if (!normalized || (!GUID_RE.test(normalized) && !APP_ID_SLUG_RE.test(normalized))) {
    block('validateRoute', 'invalid_appId', '/');
  }
}

/**
 * When a mid-session client switch's role-mapping fetch fails, resolve the home
 * href of the previous client to revert to (legacy `getFallbackClientUrl`
 * parity). Returns null (→ caller uses the soft home fallback) unless the
 * previous client is a real, still-accessible client whose mapping is ALREADY
 * cached — that last condition keeps the revert loop-safe, because the redirect
 * target is then guaranteed to resolve from cache without another fetch.
 */
function clientSwitchRevertHref(failedClientId: string): string | null {
  let prevId: string | null = null;
  try {
    prevId = localStorage.getItem(CLIENT_STORAGE_KEYS.localPrevious);
  } catch {
    return null;
  }
  if (!prevId || prevId === failedClientId) return null;
  if (!useClientStore.getState().available.some((c) => c.id === prevId)) return null;
  if (!hasRoleScreens(prevId)) return null;
  return `/${prevId}/app-container/home`;
}

/**
 * Per-MFE route AUTHORISATION gate — port of legacy `ValidateRouteGuard`
 * (`raptor-shell-ui/.../guards/validate-route.guard.ts`) +
 * `AuthorizeService.validateRoute`. Runs on the canonical client-scoped mount
 * routes: an appId absent from the user's role-screen mapping for the active
 * client is blocked. Access denied via a direct URL lands on the client's home
 * MFE (`/{clientId}/app-container/home`) with a toast; legacy sent `/error`.
 *
 * `screenPath` (optional) is the FULL legacy screen path to validate against —
 * admin routes pass `admin/apps/{appId}` so the check matches legacy
 * `ValidateRouteGuard`, which built its screen name from every URL segment
 * after `app-container`. Non-admin routes validate the bare appId.
 *
 * Failure model (legacy-parity, with one deliberate improvement):
 *  - INVALID client in the URL (clientId not in the resolved available list, e.g.
 *    a tampered URL): the role call is SKIPPED (it would 401) and access is NOT
 *    blocked — the component-level `clientAccessRedirect` bounces to a valid
 *    available client's home (legacy BaseRouteGuard fallback, PRTL-1204).
 *  - FIRST login (session never bootstrapped): this is where the first mapping
 *    fetch actually happens (legacy `applyRoleMappingForCurrentClient` at
 *    frame-redirect — bypassed here by `navigateToLoginRequestUrl`), so a failure
 *    BLOCKS ENTRY → `/login` (even essential surfaces), matching legacy's
 *    LOGIN_URL. `LoginPage` clears the interaction flag on click, so the user
 *    re-enters as soon as the endpoint recovers.
 *  - Established session (bootstrapped once, session-persisted so a REFRESH or
 *    SignalR cache-clear does NOT re-block): a fetch failure = a mid-session
 *    client switch (token still rotating) or a cleared cache. Legacy fell back to
 *    a valid client rather than logging out, so we REVERT to the previous client
 *    (only when its mapping is still cached, so the redirect target is guaranteed
 *    not to re-fetch/re-fail). When there is no known-good previous client,
 *    essential shell surfaces (home/workspace) + allowlisted MFEs pass and
 *    everything else falls back to the client's home MFE — never a `/login`
 *    bounce (that would strand a user who is already in).
 *  - An EMPTY mapping (a successful fetch listing zero screens) is treated as a
 *    denial (legacy parity): non-essential screens → toast + client home, while
 *    essential/allowlisted surfaces stay open so the shell is never walled off.
 *  - The IMPROVEMENT over legacy: a screen absent from a loaded mapping lands on
 *    the client's home MFE with a toast (keep the user inside the shell) instead
 *    of legacy's `/error`.
 */
export async function validateScreenAccess(
  appId: string,
  clientId: string | null,
  screenPath?: string,
): Promise<void> {
  validateRoute(appId);
  if (!clientId) return;

  // Row #7 (legacy BaseRouteGuard checks client validity BEFORE the role fetch):
  // if the client list has resolved and this clientId is NOT one of the user's
  // accessible clients (e.g. a tampered URL), the role-mapping call would 401 for
  // a client they can't access. Do NOT fetch and do NOT block — return so the
  // component-level `clientAccessRedirect` bounces to a valid available client's
  // home (fallback). Only enforce once the list is known (non-empty); before it
  // resolves we can't tell, so we proceed (the default landing client is valid).
  const available = useClientStore.getState().available;
  if (available.length > 0 && !available.some((c) => c.id === clientId)) {
    getTracker().event('route_guard_blocked', {
      guard: 'validateScreenAccess',
      reason: 'client_not_in_available_defer_fallback',
      redirect_target: 'clientAccessRedirect',
    });
    return;
  }

  const subject = screenPath ?? appId;

  let screens: readonly string[];
  try {
    screens = await ensureRoleScreens(clientId);
  } catch {
    // Cache MISS failure (post eager-load this means a mid-session client switch
    // whose token is still rotating, or a SignalR cache-clear). Legacy fell back
    // to a valid client rather than logging out. FIRST choice: revert to the
    // previous client (only when its mapping is still cached — guarantees the
    // redirect target won't re-fetch/re-fail into a loop).
    const revertHref = clientSwitchRevertHref(clientId);
    if (revertHref) {
      useClientStore.getState().resetToPrevious();
      getTracker().event('route_guard_blocked', {
        guard: 'validateScreenAccess',
        reason: 'role_mapping_fetch_failed_switch_revert',
        redirect_target: revertHref,
      });
      throw redirect({ href: revertHref, replace: true });
    }

    // FIRST login (session never bootstrapped): this is the login-time mapping
    // load (legacy `applyRoleMappingForCurrentClient`, which returned LOGIN_URL
    // on failure). Because `navigateToLoginRequestUrl` bypasses `FrameRedirectPage`,
    // this guard is where the first mapping fetch actually happens — so a failure
    // here must block ENTRY (even essential surfaces), not drop the user into an
    // empty shell. Bounce to /login with the sign-in button; `LoginPage` clears
    // the interaction flag on click, so the user can re-enter the moment the
    // endpoint recovers (no permanent stranding). The flag is session-persisted,
    // so a REFRESH or SignalR cache-clear AFTER a first success does NOT re-block
    // — the user rides the established session and degrades softly below.
    if (!isRoleMappingBootstrapped()) {
      markInteractionRequired();
      getTracker().event('route_guard_blocked', {
        guard: 'validateScreenAccess',
        reason: 'role_mapping_fetch_failed_first_login',
        redirect_target: '/login',
      });
      throw redirect({ to: '/login' });
    }

    // Established session (something cached) but no revert target: a role-mapping
    // fetch failure must NOT silently grant a gated screen, but must also not
    // wall off the shell or bounce to /login (that would strand a user who is
    // already in). Essential shell surfaces (home/workspace) + allowlisted MFEs
    // pass; everything else falls back to the client's home MFE.
    if (isEssentialOrAllowlistedScreen(subject)) return;
    const homeHref = `/${clientId}/app-container/home`;
    getTracker().event('route_guard_blocked', {
      guard: 'validateScreenAccess',
      reason: 'role_mapping_fetch_failed',
      redirect_target: homeHref,
    });
    throw redirect({ href: homeHref, replace: true });
  }

  // A screen absent from a loaded mapping — INCLUDING an empty mapping (legacy
  // parity: empty ⇒ every non-essential screen is denied) — lands on the
  // client's home MFE with a toast (new-shell UX: keep the user inside the
  // shell) rather than legacy's `/error`. `isScreenAllowed` still exempts
  // shell-essential surfaces (home/workspace) + allowlisted MFEs, so an empty
  // mapping never walls off the shell itself.
  if (!isScreenAllowed(subject, screens)) {
    const homeHref = `/${clientId}/app-container/home`;
    getTracker().event('route_guard_blocked', {
      guard: 'validateScreenAccess',
      reason: 'screen_not_in_role_mapping',
      redirect_target: homeHref,
    });
    pushToast({ variant: 'error', message: APP_ACCESS_DENIED_TOAST });
    throw redirect({ href: homeHref, replace: true });
  }

  // Layer 2 — tile-existence gate (legacy `AuthorizeService.isTileExistsInClient`).
  // Even when Layer 1 grants the screen, the app must be PROVISIONED + active for
  // this client. Only `workspace/apps/*` + `admin/apps/*` screen paths are
  // checked (passed via `screenPath`); bare appIds (home, workspace, mad/pie)
  // never carry a `screenPath` and short-circuit to allowed, mirroring legacy
  // `else → true`. `isTileProvisioned` degrades gracefully on an unloaded/empty
  // feed (the feed is warmed by ensureAppRegistry/ensureAdminAppRegistry in the
  // same beforeLoad), so a transient blip can't wall off the shell.
  if (screenPath && !isTileProvisioned(clientId, screenPath)) {
    const homeHref = `/${clientId}/app-container/home`;
    getTracker().event('route_guard_blocked', {
      guard: 'validateScreenAccess',
      reason: 'tile_not_in_client',
      redirect_target: homeHref,
    });
    pushToast({ variant: 'error', message: APP_ACCESS_DENIED_TOAST });
    throw redirect({ href: homeHref, replace: true });
  }
}

/**
 * Auth0 presence gate (PRTL-2857 7.2) — the ONLY provider-specific delta on top
 * of the shared authorization pipeline. For an Auth0-enabled MFE (per
 * `VITE_AUTH0_MFE_IDS`), require an active Auth0 session in addition to the MSAL
 * shell session + `validateScreenAccess` checks that already ran. No-op for
 * every MSAL MFE, so the guard chain is otherwise identical for both providers.
 *
 * On a missing Auth0 session it starts an interactive `loginWithRedirect` with
 * `audience` + `redirect_uri` + `paramclientid` (strictly resolved for claim
 * enrichment) and a `login_hint` derived from the shell session identity (to
 * skip the account picker when a federated session exists — PRTL-2856). Mirrors
 * `requireAuth`: fire the redirect (not awaited) and throw a TanStack redirect
 * so the router halts while the full-page Auth0 navigation takes over.
 */
export async function ensureAuth0ForMfe(appId: string, clientId: string | null): Promise<void> {
  if (!isAuth0App(appId)) return;
  if (await isAuth0Authenticated()) return;

  const paramclientid = clientId || resolveClientIdStrict() || undefined;
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const loginHint = getLoginHint();

  getTracker().event('route_guard_blocked', {
    guard: 'ensureAuth0ForMfe',
    reason: 'no_auth0_session',
    redirect_target: 'auth0_login',
  });

  void loginToAuth0({ paramclientid, returnTo, loginHint: loginHint ?? undefined });
  throw redirect({ to: '/login', replace: true });
}

// Re-export the redirect helper for routes that need to wire custom checks.
export { block as denyRoute };

// Lint placeholder — keeps tree-shaking honest if a consumer imports just types.
export type { AnyRoute };
